// Single source of truth for both the home page and the resume page.
// Everything here comes from the CV; edit this file, not the components.

export const profile = {
  name: "Berkay Gemici",
  fullName: "Berkay Gemici",
  tagline: "I build mobile apps and ship software.",
  location: "Braunschweig, Lower Saxony, 38106",
  phone: "+49 152 31390961",
  email: "berkayygemici@gmail.com",
  // Displayed without the www; the link itself points at the canonical host.
  site: "berkaygemici.com",
  siteUrl: "https://www.berkaygemici.com/",
  intro: [
    "Business informatics student at TU Braunschweig and a working student DevOps engineer at Siemens Mobility.",
    "Co-founder of Catyard Studio, where I build AI-powered iOS apps that passed 100K+ downloads in the last year.",
  ],
};

export const social = {
  github: "https://github.com/berkaygemici",
  linkedin: "https://www.linkedin.com/in/berkay-gemici/",
  twitter: "https://twitter.com/berkayygemicii",
};

// Outbound links reused across the timeline copy.
const url = {
  siemens: "https://www.mobility.siemens.com/",
  newyorker: "https://www.newyorker.de/",
  catyard: "https://catyardstudio.com/",
  mobilescreen: "https://mobilescreen.co/",
  tubs: "https://www.tu-braunschweig.de/",
  tau: "https://tau.edu.tr/",
  furkan: "https://furkansuren.com/",
};

// Home page timeline — reverse chronological.
// `body` is an array: plain strings render as text, { t, href } renders as a link.
export const timeline = [
  {
    date: "Apr 2026 —",
    title: "Siemens Mobility",
    logo: require("../static/images/logos/siemens.png"),
    body: [
      "Working Student DevOps Engineer at ",
      { t: "Siemens Mobility", href: url.siemens },
      ". Designing and optimizing CI/CD pipelines, writing automation scripts, and managing Docker-based container environments together with R&D teams in Brunswick.",
    ],
  },
  {
    date: "Jan 2026 —",
    title: "MobileScreen",
    href: url.mobilescreen,
    logo: require("../static/images/logos/mobilescreen.png"),
    body: [
      "Founded ",
      { t: "MobileScreen", href: url.mobilescreen },
      " with ",
      { t: "Furkan Suren", href: url.furkan },
      ". It's an AI b2b SaaS that helps mobile app businesses automate their App Store screenshot design process. This was originally built as an internal tool for ",
      { t: "Catyard Studio", href: url.catyard },
      ". Applied to YC Spring 2026 batch and actively seeking seed funding.",
    ],
  },
  {
    date: "Apr 2024 — Apr 2026",
    title: "NEW YORKER Information Services GmbH",
    logo: require("../static/images/logos/newyorker.png"),
    body: [
      "Working Student at ",
      { t: "NEW YORKER", href: url.newyorker },
      ". Built and maintained an internal tool used across NewYorker stores worldwide with .NET C#, Go, Entity Framework and Angular, along with the CI/CD pipelines, unit tests and code reviews around it.",
    ],
  },
  {
    date: "Apr 2023 —",
    title: "Catyard Studio",
    href: url.catyard,
    logo: require("../static/images/logos/catyard.png"),
    body: [
      "Co-Founder and Lead Developer at ",
      { t: "Catyard Studio", href: url.catyard },
      ". 20 AI-powered iOS apps built from scratch — 100K+ downloads in the last year at a 4.7 average rating.",
    ],
  },
  {
    date: "Apr 2023 —",
    title: "TU Braunschweig",
    logo: require("../static/images/logos/tubs.png"),
    body: [
      "Studying B.Sc. Business Informatics at ",
      { t: "TU Braunschweig", href: url.tubs },
      " in Braunschweig, Germany.",
    ],
  },
  {
    date: "Sep 2021 — Apr 2023",
    title: "Turkish-German University",
    logo: require("../static/images/logos/tau.png"),
    body: [
      "Studied B.Sc. Civil Engineering at ",
      { t: "Turkish-German University", href: url.tau },
      " in Istanbul, Turkey.",
    ],
  },
];

export const education = [
  {
    school: "Technical University of Braunschweig",
    degree: "B.Sc. Business Informatics",
    location: "Braunschweig, Germany",
    date: "April 2023 — ",
  },
  {
    school: "Turkish-German University",
    degree: "B.Sc. Civil Engineering",
    location: "Istanbul, Turkey",
    date: "September 2021 — April 2023",
  },
];

export const experience = [
  {
    role: "Working Student DevOps Engineer",
    company: "Siemens Mobility",
    location: "Brunswick, Germany",
    date: "April 2026 — ",
    bullets: [
      "Designed and optimized CI/CD pipelines for automated software build, testing, and deployment processes.",
      "Developed automation scripts to streamline workflows and reduce manual tasks.",
      "Managed and enhanced Docker-based container environments for reliable development and deployment.",
      "Improved internal DevOps tooling and processes in collaboration with R&D teams.",
    ],
  },
  {
    role: "Working Student",
    company: "NEW YORKER Information Services GmbH",
    location: "Braunschweig, Germany",
    date: "April 2024 — April 2026",
    bullets: [
      "Developed and maintained an internal tool used across NewYorker stores worldwide using .NET C#, Go, Entity Framework, and Angular.",
      "Implemented automated CI/CD pipelines, unit tests, and code reviews to improve software quality and deployment processes.",
      "Collaborated with cross-functional teams in an agile environment to deliver stable and user-focused features.",
      "Applied clean code principles, dependency injection, and Git-based workflows for maintainable software development.",
    ],
  },
  {
    role: "Co-Founder / Lead Developer",
    company: "Catyard Studio",
    location: "Remote",
    date: "April 2023 — ",
    href: "https://catyardstudio.com/",
    bullets: [
      "Co-founded the studio and shipped 20 iOS apps from scratch, coding daily across the whole portfolio.",
      "Designed and implemented architecture, features, and backend/frontend functionality for all projects.",
      "Delivered user-focused applications, managing every aspect of development.",
      "Achieved over 100K+ downloads in the past year at a 4.7 average rating.",
    ],
  },
];

export const projectsNote =
  "Selected work. Catyard Studio has shipped 20 iOS apps in total.";

export const projects = [
  {
    title: "MobileScreen",
    date: "2026 — ",
    href: "https://mobilescreen.co/",
    body: "Tooling that generates App Store and Google Play screenshots and marketing assets for mobile developers.",
  },
  {
    title: "Cartoony",
    date: "2025",
    href: "https://apps.apple.com/us/app/cartoony-anime-cartoon-maker/id6743962340",
    body: "AI-powered photo transformer that turns pictures into cartoon and anime styles.",
  },
  {
    title: "Einstein AI",
    date: "2024",
    href: "https://apps.apple.com/us/app/einstein-ai-learn-memorize/id6739162816",
    body: "Study companion that helps students with classes, reads PDFs, and summarizes content.",
  },
  {
    title: "Fish Identifier",
    date: "2024",
    href: "https://apps.apple.com/us/app/fish-identifier-gpt-ai-camera/id6737740970",
    body: "Identifies fish species from the camera using AI-powered image recognition.",
  },
  {
    title: "Rock Identifier",
    date: "2024",
    href: "https://apps.apple.com/tr/app/rock-identifier-stone-value/id6736365953",
    body: "Rock and mineral identification tool that determines stone types and their value.",
  },
  {
    title: "Math Solver",
    date: "2024",
    href: "https://apps.apple.com/us/app/ai-math-calculator-mathbot/id6682705979",
    body: "AI-powered calculator that solves complex math problems step by step.",
  },
  {
    title: "Orbit",
    date: "2024",
    href: "https://apps.apple.com/us/app/orbit-gpt-o1-chat-assistant/id6612007544?platform=iphone",
    body: "Mobile chat assistant for productivity and instant information.",
  },
];

export const skills = [
  {
    label: "Programming Languages",
    value: "Python, JavaScript, Go, Java, C#, C++",
  },
  { label: "Web Technologies", value: "HTML5, CSS3, JavaScript" },
  {
    label: "Frameworks and Libraries",
    value: "React.js, Django, Flask, Angular, React Native",
  },
  { label: "Databases", value: "MySQL, PostgreSQL, MongoDB" },
  {
    label: "Cloud Technologies",
    value: "AWS RDS, AWS Lambda, AWS S3, Firebase",
  },
  { label: "Tools", value: "Git, Docker, Jira, Confluence, Grafana, Jenkins" },
];

export const languages = [
  { label: "German", value: "C1" },
  { label: "English", value: "C1" },
  { label: "Turkish", value: "Native" },
];
